import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Plus, Loader2, ExternalLink, Mic, Bike, Smartphone, Image as ImageIcon, Upload as UploadIcon, X, BellRing, Power, Trash2, Copy, Layers, Phone, Pencil, Globe, FileText, ChevronDown, ChevronRight, Store, Undo2 } from "lucide-react";
import { api } from "../../lib/api";
import { Field, Modal } from "../admin/AdminRestaurants";
import { toast } from "sonner";
import TenantSearch, { useTenantSearch } from "../../components/TenantSearch";
import AdminLimitControl from "./_partials/AdminLimitControl";
import TenantFirebasePanel, {
    FirebaseAppExistingForm, EMPTY_APP_EXISTING, clientValidateApp,
} from "./_partials/TenantFirebasePanel";

// iter70 — Base URL for legal pages submitted to Play Store / App Store.
// Reviewers always hit production, so we hardcode the production
// marketing domain even when the Super Admin panel itself is being
// viewed on preview. Override via REACT_APP_LEGAL_BASE_URL when the
// platform domain changes.
const LEGAL_BASE_URL = (process.env.REACT_APP_LEGAL_BASE_URL || "https://marketplace.localappstore.in").replace(/\/+$/, "");

// 2026-08-20 — tenant-scoped Shiprocket webhook base. Super Admin gets a
// read-only copy of the same URL AdminShiprocket.jsx shows the tenant
// admin, purely so support/ops can hand it to a tenant without needing
// to log into their console. See routers/shiprocket_webhook.py.
const SHIPROCKET_WEBHOOK_BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/webhook/shiprocket`;

/** Returns the four store-submission URLs for a tenant (Customer +
 *  Rider × Privacy + Terms). Tenant-scoped via ``?tenant=`` so the
 *  LegalPage renders THAT tenant's effective policy markdown. The
 *  ``&app=rider`` flag distinguishes the rider build's submission
 *  URL — same content today, but lets us serve rider-specific
 *  policies later without changing the URLs that are already on
 *  the stores. */
function legalUrls(slug) {
    if (!slug) return null;
    return {
        customer_privacy: `${LEGAL_BASE_URL}/legal/privacy?tenant=${slug}`,
        customer_terms:   `${LEGAL_BASE_URL}/legal/terms?tenant=${slug}`,
        rider_privacy:    `${LEGAL_BASE_URL}/legal/privacy?tenant=${slug}&app=rider`,
        rider_terms:      `${LEGAL_BASE_URL}/legal/terms?tenant=${slug}&app=rider`,
    };
}

/** Compute the public-facing customer app URL for a tenant.
 *
 * Format priority (iter95 — user feedback Feb 2026):
 *   1. ``tenant.customer_app_url`` — explicit per-tenant override. Always
 *      wins so admins can set a fully-branded custom domain like
 *      ``https://apnibachat.com``.
 *   2. ``https://{slug}.{platform-host}`` — subdomain format. Preferred
 *      because: (a) it's branded (slug appears in the bar before the
 *      platform host), (b) it relies on the wildcard DNS we already
 *      provision (``*.bizildispatch.localappstore.in``), (c) the
 *      frontend's tenant resolver picks up the subdomain natively, no
 *      query string needed.
 *   3. ``{current_origin}/?tenant={slug}`` — query-string fallback for
 *      environments where wildcard DNS isn't viable (localhost, raw IP,
 *      Emergent preview ephemeral hosts).
 *
 * The "platform host" is derived from (in order):
 *   - ``REACT_APP_PLATFORM_HOST`` env var (authoritative — set by the
 *     operator at deploy time),
 *   - else the current ``window.location.hostname`` stripped of any
 *     leading subdomain (so visiting ``admin.bizil.com`` still
 *     produces ``slug.bizil.com``).
 */
function tenantPublicUrl(t) {
    const slug = t?.slug || "";
    const explicit = t?.customer_app_url || "";
    if (explicit) {
        return explicit.startsWith("http") ? explicit
            : `${window.location.origin}${explicit.startsWith("/") ? "" : "/"}${explicit}`;
    }

    const proto = window.location.protocol || "https:";
    const currentHost = window.location.hostname || "";

    // Localhost / raw IPs / Emergent's ephemeral preview hosts can't
    // serve ``*.<host>`` wildcard subdomains — keep the query-string
    // form for those so the link is actually reachable.
    const isLocalOrIp =
        currentHost === "localhost" ||
        currentHost === "127.0.0.1" ||
        /^\d+\.\d+\.\d+\.\d+$/.test(currentHost) ||
        currentHost.endsWith(".emergentagent.com") ||
        currentHost.endsWith(".emergent.host");

    if (isLocalOrIp || !slug) {
        return `${window.location.origin}/?tenant=${slug}`;
    }

    // Determine the platform host. **Always use the current hostname**
    // — that's what the operator is browsing from RIGHT NOW, which is
    // the natural choice for "show me URLs to share". This also
    // handles the multi-domain alias case (iter95 user feedback): a
    // single build can be served by several hostnames (e.g.
    // ``bizildispatch.localappstore.in`` + ``marketplace.localappstore.in``
    // pointing at the same nginx). Visiting ``marketplace.../super``
    // should produce ``slug.marketplace...``, NOT
    // ``slug.bizildispatch...`` (which is what would happen if we
    // honoured ``REACT_APP_PLATFORM_HOST`` here — that env var is the
    // build-time default for tenant-from-subdomain RESOLUTION, not a
    // hard pin on URL GENERATION).
    //
    // Caveat: if your super-admin sits on a DIFFERENT subdomain
    // (e.g. ``admin.bizildispatch.localappstore.in``) and you want
    // tenant URLs on the BARE platform host (``slug.bizildispatch...``),
    // set ``tenant.customer_app_url`` explicitly per tenant — that
    // override (path #1 above) always wins. There is no global env-var
    // path for this because the alias-domain case is the common one
    // and the "admin-on-subdomain" case is rare; opt-in per tenant
    // beats opt-out for all of them.
    const baseHost = currentHost;

    // Preserve a non-standard port if the operator runs the app on one.
    const port = window.location.port && window.location.port !== "80" && window.location.port !== "443"
        ? `:${window.location.port}` : "";

    return `${proto}//${slug}.${baseHost}${port}`;
}

export default function SuperTenants() {
    const [rows, setRows] = useState([]);
    const [plans, setPlans] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [form, setForm] = useState({
        slug: "", name: "", contact_mobile: "",
        primary_hsl: "142 71% 45%", accent_hsl: "39 100% 50%",
        plan: "STARTER", logo_url: "",
        clone_from_slug: "",
        // iter134y — opt-in flags: whether the create-time clone
        // also brings across shops + products from the source tenant.
        // Products depend on shops (they reference restaurant_id) so
        // the backend force-enables shops when products is checked.
        //
        // iter134y2 — defaults flipped ON when a source is selected.
        // User feedback: "I selected Bizil Foods as the source to
        // copy products from, but products weren't copied". They
        // reasonably expect that selecting a source = copy the whole
        // thing. The checkboxes now default CHECKED and act as
        // opt-outs for operators who want an identity-only clone.
        // Shops always land as INACTIVE with owner mobile/bank fields
        // stripped, so this is safe by default.
        clone_include_shops: true,
        clone_include_products: true,
    });
    // iter74 — optional EXISTING-Firebase blocks for Create Tenant.
    // Per-app: customer + rider can independently be AUTO (default) or
    // EXISTING. When EXISTING is selected for an app, the form below
    // is filled and sent as ``firebase_{app}_existing`` on submit.
    const [firebaseCustomerMode, setFirebaseCustomerMode] = useState("AUTO");
    const [firebaseRiderMode, setFirebaseRiderMode] = useState("AUTO");
    const [firebaseCustomerForm, setFirebaseCustomerForm] = useState({ ...EMPTY_APP_EXISTING });
    const [firebaseRiderForm, setFirebaseRiderForm] = useState({ ...EMPTY_APP_EXISTING });

    const load = () => api.get("/super/tenants").then(({ data }) => setRows(data));
    const loadPlans = () => api.get("/super/plans").then(({ data }) => setPlans(data));
    useEffect(() => { load(); loadPlans(); }, []);

    const save = async () => {
        // iter74 — per-app EXISTING blocks. Validate each app's form
        // client-side before sending.
        let payload = { ...form };
        const errs = [];
        if (firebaseCustomerMode === "EXISTING") {
            const e = clientValidateApp(firebaseCustomerForm);
            if (e.length) errs.push(`Customer app: ${e[0]}`);
            else payload.firebase_customer_existing = firebaseCustomerForm;
        }
        if (firebaseRiderMode === "EXISTING") {
            const e = clientValidateApp(firebaseRiderForm);
            if (e.length) errs.push(`Rider app: ${e[0]}`);
            else payload.firebase_rider_existing = firebaseRiderForm;
        }
        if (errs.length) {
            toast.error(errs.join(" · "));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/super/tenants", payload);
            if (data?.clone_result) {
                const v = data.clone_result.verticals || {};
                const h = data.clone_result.hero_tiles || {};
                // iter134y — surface shops + products counts in the
                // toast so the operator can confirm at a glance that
                // their "Copy Products from Existing Tenant" checkbox
                // actually did the work. Previously the toast only
                // mentioned verticals + tiles, hiding the fact that
                // products silently skipped.
                const s = data.clone_result.shops || {};
                const p = data.clone_result.products || {};
                const pc = data.clone_result.product_categories || {};
                const bits = [
                    `${v.inserted || 0} verticals`,
                    `${h.inserted || 0} tiles`,
                ];
                if (s.inserted) bits.push(`${s.inserted} shops`);
                if (pc.inserted) bits.push(`${pc.inserted} menu cats`);
                if (p.inserted) bits.push(`${p.inserted} products`);
                bits.push("branding/workflow/coins/referral synced");
                toast.success(`Tenant created + cloned from ${form.clone_from_slug}: ${bits.join(", ")}`);
            } else if (firebaseCustomerMode === "EXISTING" || firebaseRiderMode === "EXISTING") {
                const parts = [];
                if (firebaseCustomerMode === "EXISTING") parts.push("customer");
                if (firebaseRiderMode === "EXISTING") parts.push("rider");
                toast.success(`Tenant created · ${parts.join(" + ")} on existing Firebase`);
            } else {
                toast.success("Tenant created");
            }
            setOpen(false);
            setForm({
                slug: "", name: "", contact_mobile: "",
                primary_hsl: "142 71% 45%", accent_hsl: "39 100% 50%",
                plan: "STARTER", logo_url: "",
                clone_from_slug: "",
                // iter134y2 — same defaults-ON as the initial state so
                // opening the modal again pre-selects the "copy the
                // whole thing" behaviour operators expect.
                clone_include_shops: true,
                clone_include_products: true,
            });
            setFirebaseCustomerMode("AUTO");
            setFirebaseRiderMode("AUTO");
            setFirebaseCustomerForm({ ...EMPTY_APP_EXISTING });
            setFirebaseRiderForm({ ...EMPTY_APP_EXISTING });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const toggleVoice = async (t) => {
        const next = !(t.features?.voice_search);
        setRows((prev) => prev.map((r) => r.id === t.id
            ? { ...r, features: { ...(r.features || {}), voice_search: next } }
            : r));
        try {
            await api.patch(`/super/tenants/${t.id}/features`, { voice_search: next });
            toast.success(`${t.name}: voice search ${next ? "enabled" : "disabled"}`);
        } catch {
            toast.error("Failed to update");
            load();
        }
    };

    const setRiderMode = async (t, mode) => {
        // Optimistic update first so the dropdown feels snappy; revert on error.
        setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, rider_mode: mode } : r));
        try {
            await api.patch(`/super/tenants/${t.id}/rider-mode`, { rider_mode: mode });
            toast.success(`${t.name}: rider mode → ${mode}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to set rider mode");
            load();
        }
    };

    // iter104 — flip a tenant between SINGLE_VENDOR and MULTI_VENDOR.
    // Backend refuses SINGLE_VENDOR when the tenant currently has >1
    // shop; we surface the 409 message verbatim so the operator knows
    // exactly what to clean up before retrying.
    const setVendorMode = async (t, mode) => {
        if (mode === "SINGLE_VENDOR" && !window.confirm(
            `Switch "${t.name}" to Single-Vendor mode?\n\n` +
            `• Customers will land directly inside the tenant's single shop\n` +
            `• Browse / vertical / category pages are hidden\n` +
            `• Merchant "+ Add Shop" is disabled beyond the 1st shop\n\n` +
            `Confirm switch?`,
        )) return;
        setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, vendor_mode: mode } : r));
        try {
            await api.patch(`/super/tenants/${t.id}/vendor-mode`, { vendor_mode: mode });
            toast.success(`${t.name}: vendor mode → ${mode.replace("_", " ").toLowerCase()}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to set vendor mode");
            load();
        }
    };

    // iter124 — Shiprocket master toggle. Optimistic UI flip → PATCH.
    // Disabling clears the cached token server-side (see backend note)
    // but preserves stored credentials so re-enabling is a single click.
    const toggleShiprocket = async (t, enabled) => {
        setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, shiprocket_enabled: enabled } : r));
        try {
            await api.patch(`/super/tenants/${t.id}/shiprocket-toggle`, { enabled });
            toast.success(`${t.name}: Shiprocket ${enabled ? "enabled" : "disabled"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to toggle Shiprocket");
            load();
        }
    };

    // Order Returns master toggle. Same optimistic-flip pattern as
    // Shiprocket above — once enabled, the Tenant Admin configures the
    // return window from the new Admin → Returns page.
    const toggleReturns = async (t, enabled) => {
        setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, returns_enabled: enabled } : r));
        try {
            await api.patch(`/super/tenants/${t.id}/returns-toggle`, { enabled });
            toast.success(`${t.name}: Order Returns ${enabled ? "enabled" : "disabled"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to toggle Order Returns");
            load();
        }
    };

    const impersonate = async (t) => {
        // Open the popup **synchronously** so the browser keeps the user
        // gesture context — otherwise the post-await window.open() is blocked
        // by most browsers and the click feels like a no-op.
        const win = window.open("about:blank", "_blank");
        try {
            const { data } = await api.post(`/super/tenants/${t.id}/impersonate`);
            const url = new URL(window.location.origin);
            url.searchParams.set("tenant", t.slug);
            url.searchParams.set("impersonate", data.token);
            url.pathname = "/admin";
            if (win) {
                win.location.replace(url.toString());
            } else {
                window.open(url.toString(), "_blank", "noopener");
            }
            toast.success(`Opening preview as ${t.name}`);
        } catch (e) {
            if (win) win.close();
            toast.error(e?.response?.data?.detail || "Could not impersonate");
        }
    };

    const toggleStatus = async (t) => {
        // ACTIVE → SUSPENDED ("disable" — the tenant's storefront returns 503
        // for customers + logged-out admins can't sign in). SUSPENDED → ACTIVE.
        const next = t.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED";
        const verb = next === "ACTIVE" ? "Enable" : "Disable";
        if (!window.confirm(`${verb} ${t.name}? Customers will ${next === "ACTIVE" ? "regain" : "lose"} access immediately.`)) return;
        try {
            await api.patch(`/super/tenants/${t.id}`, { status: next });
            toast.success(`${t.name} ${verb.toLowerCase()}d`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Status change failed");
        }
    };

    const deleteTenant = async (t) => {
        if (!window.confirm(`Delete ${t.name}? This soft-deletes the tenant (data is retained for 30 days). Type the slug to confirm.`)) return;
        const typed = window.prompt(`Type "${t.slug}" to confirm permanent deletion:`);
        if (typed !== t.slug) {
            toast.info("Cancelled — slug mismatch.");
            return;
        }
        try {
            await api.delete(`/super/tenants/${t.id}`);
            toast.success(`${t.name} deleted`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const copyUrl = async (url, label = "URL") => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success(`${label} copied`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not copy");
        }
    };

    const [cloneTarget, setCloneTarget] = useState(null);   // tenant being cloned INTO
    const [adminEditor, setAdminEditor] = useState(null);   // tenant whose TENANT_ADMIN is being set/changed
    const copyVerticals = async (t) => {
        if (t.slug === "bizil") { toast.error("This IS the default tenant — copy from it, not to it."); return; }
        setCloneTarget(t);
    };

    const { query: search, setQuery: setSearch, filter: filterBySearch } = useTenantSearch();
    const visibleRows = filterBySearch(
        showDeleted ? rows : rows.filter((t) => t.status !== "DELETED"),
    );
    // For the Create Tenant modal: any non-custom plan that is not archived.
    // Custom plans are tenant-scoped, so they only show up in Edit Branding
    // for the matching tenant.
    const planOptions = plans.filter((p) => p.status !== "ARCHIVED" && !p.is_custom);

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h1 className="text-2xl font-bold">Tenants</h1><p className="text-sm text-muted-foreground mt-1">{visibleRows.length} of {rows.length} • White-label clients</p></div>
                <div className="flex items-center gap-2 flex-wrap">
                    <TenantSearch value={search} onChange={setSearch} placeholder="Search by name or slug…" testid="super-tenants-search" />
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground cursor-pointer">
                        <input
                            data-testid="show-deleted-toggle"
                            type="checkbox"
                            className="accent-primary w-4 h-4"
                            checked={showDeleted}
                            onChange={(e) => setShowDeleted(e.target.checked)}
                        />
                        Show deleted
                    </label>
                    <button data-testid="add-tenant" onClick={() => setOpen(true)} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Create Tenant</button>
                </div>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleRows.map((t) => {
                    const publicUrl = tenantPublicUrl(t);
                    const statusBadge =
                        t.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
                        t.status === "SUSPENDED" ? "bg-amber-100 text-amber-700" :
                        t.status === "DELETED" ? "bg-rose-100 text-rose-700" :
                        "bg-muted text-muted-foreground";
                    return (
                    <div key={t.id} data-testid={`tenant-card-${t.slug}`} className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-lg truncate" title={t.name}>{t.name}</p>
                                <a
                                    href={publicUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`tenant-url-${t.slug}`}
                                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 group max-w-full"
                                    title={publicUrl}
                                >
                                    <ExternalLink size={10} className="flex-shrink-0" />
                                    <span className="truncate font-mono">{publicUrl.replace(/^https?:\/\//, "")}</span>
                                </a>
                                <button
                                    type="button"
                                    onClick={() => copyUrl(publicUrl)}
                                    data-testid={`tenant-url-copy-${t.slug}`}
                                    className="ml-1 text-muted-foreground hover:text-primary"
                                    title="Copy URL"
                                >
                                    <Copy size={11} />
                                </button>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${statusBadge}`}>{t.status}</span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs" data-testid={`tenant-slug-${t.slug}`}>
                            <span className="text-muted-foreground">Slug:</span>
                            <span className="font-mono font-semibold text-foreground">{t.slug}</span>
                            <button
                                type="button"
                                onClick={() => copyUrl(t.slug, "Slug")}
                                data-testid={`tenant-slug-copy-${t.slug}`}
                                className="text-muted-foreground hover:text-primary"
                                title="Copy tenant slug"
                            >
                                <Copy size={11} />
                            </button>
                        </div>
                        <div className="flex gap-2 mt-3 items-center">
                            <div className="w-7 h-7 rounded-lg border border-border" style={{ background: `hsl(${t.branding?.primary_hsl || "142 71% 45%"})` }} />
                            <div className="w-7 h-7 rounded-lg border border-border" style={{ background: `hsl(${t.branding?.accent_hsl || "39 100% 50%"})` }} />
                            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground px-2 py-1 rounded-full bg-muted">{t.plan}</span>
                        </div>
                        {t.admin_phone ? (
                            <div className="mt-2.5 flex items-center gap-1.5 text-xs flex-wrap" data-testid={`tenant-admin-phone-${t.slug}`}>
                                <Phone size={12} className="text-muted-foreground flex-shrink-0" />
                                <span className="text-muted-foreground">Admin:</span>
                                <span className="font-mono font-semibold text-foreground">+91 {t.admin_phone}</span>
                                {t.admin_name && (
                                    <span className="text-muted-foreground truncate">&middot; {t.admin_name}</span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => copyUrl(t.admin_phone)}
                                    data-testid={`tenant-admin-phone-copy-${t.slug}`}
                                    className="text-muted-foreground hover:text-primary"
                                    title="Copy admin phone"
                                >
                                    <Copy size={11} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAdminEditor(t)}
                                    data-testid={`tenant-admin-edit-${t.slug}`}
                                    className="text-muted-foreground hover:text-primary ml-auto"
                                    title="Change tenant admin"
                                >
                                    <Pencil size={11} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setAdminEditor(t)}
                                data-testid={`tenant-admin-set-${t.slug}`}
                                className="mt-2.5 w-full inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300 rounded-lg px-2.5 py-1.5 hover:bg-amber-100 dark:hover:bg-amber-950/50"
                            >
                                <Phone size={12} /> Set Tenant Admin mobile
                                <span className="text-amber-600/70 dark:text-amber-400/70 font-normal text-[10px] ml-auto">No admin yet</span>
                            </button>
                        )}
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <button
                                data-testid={`impersonate-${t.slug}`}
                                onClick={() => impersonate(t)}
                                className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline"
                            >
                                <ExternalLink size={12} /> Preview as tenant
                            </button>
                            {t.status !== "DELETED" && (
                                <>
                                    <button
                                        data-testid={`copy-verticals-${t.slug}`}
                                        onClick={() => copyVerticals(t)}
                                        disabled={t.slug === "bizil"}
                                        className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={t.slug === "bizil" ? "This IS the default source tenant" : "Clone categories, tiles, branding, workflow, coins & referral from another tenant"}
                                    >
                                        <Layers size={12} /> Clone tenant
                                    </button>
                                    <button
                                        data-testid={`disable-${t.slug}`}
                                        onClick={() => toggleStatus(t)}
                                        className={`text-xs font-semibold inline-flex items-center gap-1 hover:underline ${t.status === "SUSPENDED" ? "text-emerald-600" : "text-amber-600"}`}
                                        title={t.status === "SUSPENDED" ? "Re-enable this tenant" : "Disable storefront + admin login"}
                                    >
                                        <Power size={12} /> {t.status === "SUSPENDED" ? "Enable" : "Disable"}
                                    </button>
                                    <button
                                        data-testid={`delete-${t.slug}`}
                                        onClick={() => deleteTenant(t)}
                                        className="text-xs font-semibold text-rose-600 inline-flex items-center gap-1 hover:underline"
                                        title="Soft-delete this tenant"
                                    >
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Feature flags</p>
                            {/* Location Service Engine — configured on Super Admin → Features. */}
                            <Link to={`/super/features?for=${t.id}`} data-testid={`lse-link-${t.slug}`}
                                  className="flex items-center gap-2 text-xs mb-2 hover:underline">
                                <span className="font-semibold">Location Service Engine</span>
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.features?.location_service_engine?.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                                    {t.features?.location_service_engine?.enabled ? "ON" : "OFF"}
                                </span>
                                <span className="text-primary">Configure →</span>
                            </Link>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input
                                    data-testid={`voice-toggle-${t.slug}`}
                                    type="checkbox"
                                    className="accent-primary w-4 h-4"
                                    checked={!!t.features?.voice_search}
                                    onChange={() => toggleVoice(t)}
                                />
                                <Mic size={13} className="text-muted-foreground" />
                                <span className="font-semibold">Voice Search</span>
                                <span className="text-muted-foreground">· multilingual via Gemini</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs mt-2">
                                <Bike size={13} className="text-muted-foreground" />
                                <span className="font-semibold">Rider provider</span>
                                <select
                                    data-testid={`rider-mode-${t.slug}`}
                                    value={t.rider_mode || "IN_APP"}
                                    onChange={(e) => setRiderMode(t, e.target.value)}
                                    className="ml-1 bg-muted rounded-md px-2 py-1 text-[11px] font-semibold border border-border"
                                >
                                    <option value="IN_APP">Bizil in-app</option>
                                    <option value="BIZILGO">BizilGO</option>
                                    <option value="BOTH">Both</option>
                                </select>
                            </label>
                            {/* iter104 — Single-vendor vs Multi-vendor tenant mode */}
                            <label className="flex items-center gap-2 text-xs mt-2">
                                <Store size={13} className="text-muted-foreground" />
                                <span className="font-semibold">Vendor mode</span>
                                <select
                                    data-testid={`vendor-mode-${t.slug}`}
                                    value={t.vendor_mode || "MULTI_VENDOR"}
                                    onChange={(e) => setVendorMode(t, e.target.value)}
                                    className="ml-1 bg-muted rounded-md px-2 py-1 text-[11px] font-semibold border border-border"
                                >
                                    <option value="MULTI_VENDOR">Multi-vendor</option>
                                    <option value="SINGLE_VENDOR">Single-vendor</option>
                                </select>
                            </label>
                            {/* iter124 — Shiprocket per-tenant integration toggle */}
                            <label className="flex items-center gap-2 text-xs mt-2">
                                <Store size={13} className="text-muted-foreground" />
                                <span className="font-semibold">Shiprocket</span>
                                <input
                                    type="checkbox"
                                    data-testid={`shiprocket-toggle-${t.slug}`}
                                    checked={!!t.shiprocket_enabled}
                                    onChange={(e) => toggleShiprocket(t, e.target.checked)}
                                    className="ml-1 h-4 w-4 accent-primary"
                                />
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.shiprocket_enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                                    {t.shiprocket_enabled ? "Enabled" : "Disabled"}
                                </span>
                            </label>
                            {/* Read-only, copyable tenant-scoped Shiprocket webhook URL —
                                same value AdminShiprocket.jsx shows the tenant admin;
                                credentials/secret stay tenant-admin-only, this is just the
                                (non-sensitive) URL for support/ops to hand out on request. */}
                            {t.shiprocket_enabled && (
                                <div className="flex items-center gap-2 mt-1.5 ml-[21px]">
                                    <input
                                        readOnly
                                        data-testid={`shiprocket-webhook-url-${t.slug}`}
                                        value={`${SHIPROCKET_WEBHOOK_BASE}/${t.id}`}
                                        className="flex-1 min-w-0 bg-muted rounded-md px-2 py-1 text-[10px] font-mono text-muted-foreground border border-border"
                                        onFocus={(e) => e.target.select()}
                                    />
                                    <button
                                        type="button"
                                        data-testid={`shiprocket-webhook-url-copy-${t.slug}`}
                                        onClick={() => copyUrl(`${SHIPROCKET_WEBHOOK_BASE}/${t.id}`, "Webhook URL")}
                                        className="shrink-0 p-1.5 rounded-md border hover:bg-muted"
                                        title="Copy webhook URL"
                                    >
                                        <Copy size={12} />
                                    </button>
                                </div>
                            )}
                            {/* Order Returns per-tenant toggle */}
                            <label className="flex items-center gap-2 text-xs mt-2">
                                <Undo2 size={13} className="text-muted-foreground" />
                                <span className="font-semibold">Order Returns</span>
                                <input
                                    type="checkbox"
                                    data-testid={`returns-toggle-${t.slug}`}
                                    checked={!!t.returns_enabled}
                                    onChange={(e) => toggleReturns(t, e.target.checked)}
                                    className="ml-1 h-4 w-4 accent-primary"
                                />
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.returns_enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                                    {t.returns_enabled ? "Enabled" : "Disabled"}
                                </span>
                            </label>
                            {/* Admin → Team: how many admins this tenant may have active */}
                            <AdminLimitControl tenant={t} />
                        </div>
                        <MobileAppPanel tenant={t} onSaved={load} />
                        <TenantFirebasePanel tenant={t} onSaved={load} />
                        <BrandingPanel tenant={t} plans={plans} onSaved={load} />
                        <StoreLegalUrlsPanel tenant={t} onCopy={copyUrl} />
                    </div>
                );})}
            </div>
            {open && (
                <Modal title="Create Tenant" onClose={() => setOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Slug"><input data-testid="tenant-slug" className="inp" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="spicekart" /></Field>
                        <Field label="Plan">
                            <select data-testid="tenant-plan" className="inp" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                                {planOptions.length === 0 ? (
                                    <>
                                        <option value="STARTER">Starter</option>
                                        <option value="GROWTH">Growth</option>
                                        <option value="ENTERPRISE">Enterprise</option>
                                    </>
                                ) : (
                                    planOptions.map((p) => (
                                        <option key={p.id} value={p.code}>{p.name}</option>
                                    ))
                                )}
                            </select>
                        </Field>
                        <Field full label="Name"><input data-testid="tenant-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Primary HSL"><input className="inp" value={form.primary_hsl} onChange={(e) => setForm({ ...form, primary_hsl: e.target.value })} /></Field>
                        <Field label="Accent HSL"><input className="inp" value={form.accent_hsl} onChange={(e) => setForm({ ...form, accent_hsl: e.target.value })} /></Field>
                        <Field full label="Contact mobile"><input className="inp" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} /></Field>
                        <Field full label="Clone from existing tenant (optional)">
                            <select
                                data-testid="tenant-clone-from"
                                className="inp"
                                value={form.clone_from_slug}
                                onChange={(e) => setForm({ ...form, clone_from_slug: e.target.value })}
                            >
                                <option value="">— Don&apos;t clone (empty tenant) —</option>
                                {rows
                                    .filter((t) => t.status !== "DELETED")
                                    .map((t) => (
                                        <option key={t.id} value={t.slug}>{t.name} ({t.slug})</option>
                                    ))}
                            </select>
                            <p className="text-[10px] text-muted-foreground mt-1">
                                Copies verticals, hero tiles, branding presets, order workflow,
                                coins &amp; referral config, banners, coupons, shops, menu categories
                                &amp; products by default. Logo &amp; app name stay yours.
                                Cloned shops + products land as INACTIVE — review before going live.
                            </p>
                            {form.clone_from_slug && (
                                <div
                                    data-testid="tenant-clone-extras"
                                    className="mt-2 bg-muted/40 border border-border rounded-lg p-2.5 space-y-2"
                                >
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                        Also copy from source (uncheck to skip)
                                    </p>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="tenant-clone-include-shops"
                                            checked={form.clone_include_shops}
                                            onChange={(e) => setForm({
                                                ...form,
                                                clone_include_shops: e.target.checked,
                                                // Unchecking shops must also uncheck products (dependency).
                                                clone_include_products: e.target.checked ? form.clone_include_products : false,
                                            })}
                                            className="mt-0.5 shrink-0"
                                        />
                                        <span className="text-[11px] leading-tight">
                                            <span className="font-semibold block">Shops / restaurants</span>
                                            <span className="text-muted-foreground text-[10px]">Cloned as INACTIVE — no owner mobile / bank details</span>
                                        </span>
                                    </label>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="tenant-clone-include-products"
                                            checked={form.clone_include_products}
                                            onChange={(e) => setForm({
                                                ...form,
                                                clone_include_products: e.target.checked,
                                                // Products need shops → auto-check shops when products is checked.
                                                clone_include_shops: e.target.checked ? true : form.clone_include_shops,
                                            })}
                                            className="mt-0.5 shrink-0"
                                        />
                                        <span className="text-[11px] leading-tight">
                                            <span className="font-semibold block">Products + variants + menu categories</span>
                                            <span className="text-muted-foreground text-[10px]">Auto-enables shops (products reference restaurant_id). Cloned as INACTIVE templates.</span>
                                        </span>
                                    </label>
                                </div>
                            )}
                        </Field>
                        <div className="col-span-2">
                            <LogoUploader value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} />
                        </div>
                        {/* iter74 — Per-app optional Firebase configuration. */}
                        <div className="col-span-2 border-t border-border pt-3 mt-1"
                             data-testid="create-tenant-firebase-section">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
                                Firebase configuration (optional · per-app)
                            </p>
                            <p className="text-[10px] text-muted-foreground mb-2">
                                Customer and Rider apps can be configured independently. Pick "Use existing" only for the app whose Firebase project the tenant already owns.
                                Left on "Auto" (default), Bizil automatically assigns the next Firebase project with open capacity the first time this tenant is provisioned or built — no manual shard picking needed, and it skips over any project that's already full.
                            </p>
                            {[
                                { app: "customer", label: "Customer App", mode: firebaseCustomerMode, setMode: setFirebaseCustomerMode, form: firebaseCustomerForm, setForm: setFirebaseCustomerForm },
                                { app: "rider", label: "Rider App", mode: firebaseRiderMode, setMode: setFirebaseRiderMode, form: firebaseRiderForm, setForm: setFirebaseRiderForm },
                            ].map(({ app, label, mode, setMode, form: appForm, setForm: setAppForm }) => (
                                <div key={app} className="mb-3 border border-border rounded-lg p-2.5"
                                     data-testid={`create-firebase-block-${app}`}>
                                    <p className="text-[10px] font-bold mb-1.5">{label}</p>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <label className={`border rounded-lg p-1.5 cursor-pointer text-[10px] ${mode === "AUTO" ? "border-foreground bg-muted/40" : "border-border"}`}>
                                            <input type="radio" name={`create-fb-${app}`} value="AUTO"
                                                   checked={mode === "AUTO"} onChange={() => setMode("AUTO")}
                                                   className="mr-1"
                                                   data-testid={`create-firebase-${app}-auto`} />
                                            <span className="font-bold">Auto-provision later</span>
                                        </label>
                                        <label className={`border rounded-lg p-1.5 cursor-pointer text-[10px] ${mode === "EXISTING" ? "border-foreground bg-muted/40" : "border-border"}`}>
                                            <input type="radio" name={`create-fb-${app}`} value="EXISTING"
                                                   checked={mode === "EXISTING"} onChange={() => setMode("EXISTING")}
                                                   className="mr-1"
                                                   data-testid={`create-firebase-${app}-existing`} />
                                            <span className="font-bold">Use existing Firebase</span>
                                        </label>
                                    </div>
                                    {mode === "EXISTING" && (
                                        <div className="bg-muted/20 rounded p-2">
                                            <FirebaseAppExistingForm
                                                value={appForm} onChange={setAppForm}
                                                app={app} tenant={{ name: form.name }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <button data-testid="save-tenant" disabled={busy || !form.slug || !form.name} onClick={save} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />} {form.clone_from_slug ? `Create & Clone from ${form.clone_from_slug}` : (firebaseCustomerMode === "EXISTING" || firebaseRiderMode === "EXISTING" ? "Create with existing Firebase" : "Create")}</button>
                </Modal>
            )}
            {cloneTarget && (
                <CloneTenantModal
                    target={cloneTarget}
                    sources={rows.filter((t) => t.status !== "DELETED" && t.id !== cloneTarget.id)}
                    onClose={() => setCloneTarget(null)}
                    onSuccess={() => { setCloneTarget(null); load(); }}
                />
            )}
            {adminEditor && (
                <SetAdminModal
                    tenant={adminEditor}
                    onClose={() => setAdminEditor(null)}
                    onSaved={() => { setAdminEditor(null); load(); }}
                />
            )}
        </div>
    );
}


function CloneToggle({ k, label, hint, checked, onToggle }) {
    return (
        <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input
                data-testid={`clone-toggle-${k}`}
                type="checkbox"
                className="accent-primary w-4 h-4 mt-0.5"
                checked={checked}
                onChange={(e) => onToggle(e.target.checked)}
            />
            <span>
                <span className="font-semibold">{label}</span>
                {hint && <span className="block text-[10px] text-muted-foreground">{hint}</span>}
            </span>
        </label>
    );
}


function CloneTenantModal({ target, sources, onClose, onSuccess }) {
    // Default to "bizil" if it's in the list, else the first available source.
    const defaultSrc = sources.find((s) => s.slug === "bizil")?.slug || sources[0]?.slug || "";
    const [form, setForm] = useState({
        source_slug: defaultSrc,
        include_categories: true,
        include_subcategories: true,
        include_tiles: true,
        include_branding: true,
        include_workflow: true,
        include_coins_config: true,
        include_referral_config: true,
        // iter134i — storefront assets
        include_banners: true,
        include_coupons: true,
        include_shops: false,
        include_products: false,
    });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!form.source_slug) {
            toast.error("Pick a source tenant first.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/super/tenants/${target.id}/clone-from`, form);
            setResult(data);
            toast.success(`Cloned ${target.name} from ${form.source_slug}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clone failed");
        } finally { setBusy(false); }
    };

    const toggles = [
        ["include_categories", "Business categories (verticals)", "Skipped per-slug if already present"],
        ["include_subcategories", "Sub-categories", "Children inherit their parent's new id"],
        ["include_tiles", "Home / hero tiles", "Per-position; existing positions are kept"],
        ["include_branding", "Branding presets", "Colours, theme, fonts. Logo & app name stay yours."],
        ["include_workflow", "Order workflow matrix", "Only if your tenant has no workflow yet"],
        ["include_coins_config", "Coins / loyalty config", "Only if your tenant is still on defaults"],
        ["include_referral_config", "Referral config", "Only if your tenant is still on defaults"],
        ["include_banners", "Home banners", "Titles cloned; scoped shop-ids reset for you to re-attach"],
        ["include_coupons", "Coupons", "Cloned as INACTIVE — review & enable before going live"],
        ["include_shops", "Shops / restaurants", "Adds INACTIVE clones without owner mobile / bank details"],
        ["include_products", "Products + variants + menu categories", "Requires shops; cloned as INACTIVE templates"],
    ];

    return (
        <Modal title={`Clone into ${target.name}`} onClose={onClose}>
            <div className="space-y-3">
                <Field full label="Source tenant">
                    <select
                        data-testid="clone-source"
                        className="inp"
                        value={form.source_slug}
                        onChange={(e) => setForm({ ...form, source_slug: e.target.value })}
                    >
                        <option value="">— Select source —</option>
                        {sources.map((s) => (
                            <option key={s.id} value={s.slug}>{s.name} ({s.slug})</option>
                        ))}
                    </select>
                </Field>
                <div className="bg-muted/40 rounded-xl p-3 space-y-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                        What to copy (all idempotent — re-runs are safe)
                    </p>
                    {toggles.map(([k, label, hint]) => (
                        <CloneToggle
                            key={k}
                            k={k}
                            label={label}
                            hint={hint}
                            checked={!!form[k]}
                            onToggle={(v) => setForm({ ...form, [k]: v })}
                        />
                    ))}
                </div>
                {result && (
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-xl p-3 text-xs space-y-1"
                         data-testid="clone-result">
                        <p className="font-bold text-emerald-700 dark:text-emerald-300">Clone summary</p>
                        {result.verticals && !result.verticals.note && (
                            <p>Verticals: <span className="font-mono">{result.verticals.inserted}</span> added · <span className="font-mono">{result.verticals.skipped}</span> skipped</p>
                        )}
                        {result.hero_tiles && !result.hero_tiles.note && (
                            <p>Hero tiles: <span className="font-mono">{result.hero_tiles.inserted}</span> added · <span className="font-mono">{result.hero_tiles.skipped}</span> skipped</p>
                        )}
                        {result.branding?.keys_updated?.length > 0 && (
                            <p>Branding updated: <span className="font-mono">{result.branding.keys_updated.join(", ")}</span></p>
                        )}
                        {result.workflow && <p>Workflow: {result.workflow.copied ? "✓ copied" : `— ${result.workflow.reason || "skipped"}`}</p>}
                        {result.coins_config && <p>Coins config: {result.coins_config.copied ? "✓ copied" : `— ${result.coins_config.reason || "skipped"}`}</p>}
                        {result.referral_config && <p>Referral config: {result.referral_config.copied ? "✓ copied" : `— ${result.referral_config.reason || "skipped"}`}</p>}
                        {result.banners && !result.banners.skipped && (
                            <p>Banners: <span className="font-mono">{result.banners.inserted}</span> added · <span className="font-mono">{result.banners.skipped}</span> skipped</p>
                        )}
                        {result.coupons && !result.coupons.skipped && (
                            <p>Coupons: <span className="font-mono">{result.coupons.inserted}</span> added (inactive) · <span className="font-mono">{result.coupons.skipped}</span> skipped</p>
                        )}
                        {result.shops && !result.shops.skipped && (
                            <p>Shops: <span className="font-mono">{result.shops.inserted}</span> added (inactive) · <span className="font-mono">{result.shops.skipped}</span> skipped</p>
                        )}
                        {result.products && !result.products.skipped && (
                            <p>Products: <span className="font-mono">{result.products.inserted}</span> added (inactive) · <span className="font-mono">{result.products.skipped}</span> skipped</p>
                        )}
                        {result.product_categories && !result.product_categories.skipped && (
                            <p>Menu categories: <span className="font-mono">{result.product_categories.inserted}</span> added</p>
                        )}
                    </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                    <button
                        data-testid="clone-cancel"
                        onClick={result ? onSuccess : onClose}
                        className="flex-1 bg-muted text-foreground rounded-xl py-2.5 font-semibold text-sm"
                    >
                        {result ? "Done" : "Cancel"}
                    </button>
                    <button
                        data-testid="clone-run"
                        disabled={busy || !form.source_slug}
                        onClick={run}
                        className="flex-1 bg-primary text-primary-foreground rounded-xl py-2.5 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {result ? "Re-run" : "Start clone"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}


function SetAdminModal({ tenant, onClose, onSaved }) {
    const isReplacing = !!tenant.admin_phone;
    const [form, setForm] = useState({
        mobile_number: isReplacing ? "" : (tenant.contact_mobile || ""),
        first_name: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const mob = form.mobile_number.replace(/[^0-9]/g, "").slice(-10);
        if (mob.length !== 10) {
            toast.error("Enter a 10-digit Indian mobile number");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/super/tenants/${tenant.id}/admin`, {
                mobile_number: mob,
                first_name: form.first_name || undefined,
            });
            toast.success(isReplacing
                ? `Tenant admin updated for ${tenant.name}`
                : `Tenant admin created for ${tenant.name} — they can log in with +91 ${mob}`);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to set tenant admin");
        } finally { setBusy(false); }
    };

    return (
        <Modal title={isReplacing ? `Change admin · ${tenant.name}` : `Set admin · ${tenant.name}`} onClose={onClose}>
            <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                    {isReplacing
                        ? "Add an additional TENANT_ADMIN for this tenant. The existing admin is kept — both mobiles can log in to the console."
                        : "Provision a TENANT_ADMIN user for this tenant so the owner can log in to /admin. We'll send OTP to this mobile on first login."}
                </p>
                <Field full label="Tenant admin mobile (10 digits)">
                    <input
                        data-testid="set-admin-mobile"
                        className="inp"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="9876543210"
                        value={form.mobile_number}
                        onChange={(e) => setForm({ ...form, mobile_number: e.target.value.replace(/[^0-9]/g, "") })}
                    />
                </Field>
                <Field full label="Display name (optional)">
                    <input
                        data-testid="set-admin-name"
                        className="inp"
                        placeholder={`${tenant.name} Admin`}
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    />
                </Field>
                <div className="flex items-center gap-2 pt-1">
                    <button
                        data-testid="set-admin-cancel"
                        onClick={onClose}
                        className="flex-1 bg-muted text-foreground rounded-xl py-2.5 font-semibold text-sm"
                    >Cancel</button>
                    <button
                        data-testid="set-admin-save"
                        disabled={busy || form.mobile_number.replace(/[^0-9]/g, "").length !== 10}
                        onClick={submit}
                        className="flex-1 bg-primary text-primary-foreground rounded-xl py-2.5 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {isReplacing ? "Add admin" : "Provision admin"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}



function MobileAppPanel({ tenant, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        customer_app_url: tenant.customer_app_url || "",
        rider_app_url: tenant.rider_app_url || "",
        mobile_app_name_customer: tenant.mobile_app_name_customer || "",
        mobile_app_name_rider: tenant.mobile_app_name_rider || "",
        android_package_name_customer: tenant.android_package_name_customer || "",
        android_package_name_rider: tenant.android_package_name_rider || "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const upd = {};
            for (const k of Object.keys(form)) {
                if (form[k] !== "" && form[k] !== (tenant[k] || "")) upd[k] = form[k];
            }
            if (Object.keys(upd).length === 0) {
                setOpen(false);
                return;
            }
            await api.patch(`/super/tenants/${tenant.id}`, upd);
            toast.success("Mobile app config saved");
            onSaved?.();
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const hasUrls = !!(tenant.customer_app_url && tenant.rider_app_url);

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid={`mobile-panel-${tenant.slug}`}>
            <button
                data-testid={`mobile-panel-${tenant.slug}-toggle`}
                onClick={() => setOpen((x) => !x)}
                className="w-full text-left flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground"
            >
                <span className="inline-flex items-center gap-1.5">
                    <Smartphone size={11} /> Mobile apps
                    {!hasUrls && <span className="text-amber-600 normal-case tracking-normal">· using defaults</span>}
                </span>
                <span>{open ? "▾" : "▸"}</span>
            </button>
            {open && (
                <div className="mt-2 space-y-2 text-xs">
                    <MiniField label="Customer app URL"
                               testid={`mobile-${tenant.slug}-customer-url`}
                               placeholder={`/?tenant=${tenant.slug}`}
                               value={form.customer_app_url}
                               onChange={(v) => setForm({ ...form, customer_app_url: v })} />
                    <MiniField label="Rider app URL"
                               testid={`mobile-${tenant.slug}-rider-url`}
                               placeholder={`/rider?tenant=${tenant.slug}`}
                               value={form.rider_app_url}
                               onChange={(v) => setForm({ ...form, rider_app_url: v })} />
                    <MiniField label="Customer app name"
                               testid={`mobile-${tenant.slug}-customer-name`}
                               placeholder={tenant.name}
                               value={form.mobile_app_name_customer}
                               onChange={(v) => setForm({ ...form, mobile_app_name_customer: v })} />
                    <MiniField label="Rider app name"
                               testid={`mobile-${tenant.slug}-rider-name`}
                               placeholder={`${tenant.name} Driver`}
                               value={form.mobile_app_name_rider}
                               onChange={(v) => setForm({ ...form, mobile_app_name_rider: v })} />
                    <MiniField label="Customer package id"
                               testid={`mobile-${tenant.slug}-customer-pkg`}
                               placeholder={`in.localappstore.food.${tenant.slug}`}
                               value={form.android_package_name_customer}
                               onChange={(v) => setForm({ ...form, android_package_name_customer: v })} />
                    <MiniField label="Rider package id"
                               testid={`mobile-${tenant.slug}-rider-pkg`}
                               placeholder={`in.localappstore.foodrider.${tenant.slug}`}
                               value={form.android_package_name_rider}
                               onChange={(v) => setForm({ ...form, android_package_name_rider: v })} />
                    <button
                        data-testid={`mobile-${tenant.slug}-save`}
                        onClick={save}
                        disabled={busy}
                        className="mt-1 w-full bg-foreground text-background rounded-lg py-2 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                        {busy && <Loader2 size={11} className="animate-spin" />} Save mobile config
                    </button>
                    <TestPushSection tenant={tenant} />
                </div>
            )}
        </div>
    );
}


function TestPushSection({ tenant }) {
    const [mode, setMode] = useState("broadcast");      // "broadcast" | "user"
    const [scope, setScope] = useState("all");          // "all" | "customer" | "rider"
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState("");
    const [usersLoading, setUsersLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    // Lazy-load the tenant's push-targetable users when the admin
    // switches to "Specific user" mode — avoids hitting the API for
    // every tenant card on the page.
    useEffect(() => {
        if (mode !== "user" || users.length > 0) return;
        setUsersLoading(true);
        api.get(`/super/tenants/${tenant.id}/push-users`)
            .then(({ data }) => setUsers(data))
            .catch(() => toast.error("Could not load tenant users"))
            .finally(() => setUsersLoading(false));
    }, [mode, tenant.id, users.length]);

    const sendBroadcast = async () => {
        setSending(true); setResult(null);
        try {
            const body = scope === "all" ? {} : { app: scope };
            const { data } = await api.post(
                `/super/tenants/${tenant.id}/firebase/test-broadcast`, body,
            );
            setResult({ kind: "broadcast", ...data });
            if (data.total === 0) toast.info("No devices registered yet for this tenant.");
            else if (data.failed === 0) toast.success(`Delivered to ${data.sent}/${data.total} device(s)`);
            else if (data.sent === 0) toast.error(`All ${data.failed} attempts failed — see details below`);
            else toast.warning(`Partial: ${data.sent} sent · ${data.failed} failed`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test push failed");
        } finally { setSending(false); }
    };

    const sendToUser = async () => {
        if (!userId) {
            toast.info("Pick a user first."); return;
        }
        setSending(true); setResult(null);
        try {
            const body = { user_id: userId };
            if (scope !== "all") body.app = scope;
            const { data } = await api.post(
                `/super/tenants/${tenant.id}/firebase/test-user`, body,
            );
            const u = users.find((x) => x.id === userId);
            setResult({ kind: "user", user_name: u?.name, ...data });
            const totalSent = (data.sent_fcm || 0) + (data.sent_web || 0);
            const totalFailed = (data.failed_fcm || 0) + (data.failed_web || 0);
            if (totalSent + totalFailed === 0) {
                toast.info(`${u?.name || "User"} has no registered devices.`);
            } else if (totalFailed === 0) {
                toast.success(`Delivered to ${u?.name || "user"} — ${totalSent} device(s)`);
            } else if (totalSent === 0) {
                toast.error(`All ${totalFailed} attempts failed — see details below`);
            } else {
                toast.warning(`Partial: ${totalSent} sent · ${totalFailed} failed`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test push failed");
        } finally { setSending(false); }
    };

    return (
        <div className="mt-3 pt-3 border-t border-dashed border-border space-y-2"
             data-testid={`test-push-${tenant.slug}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                <BellRing size={11} /> Send test push
            </div>
            {/* Mode tabs */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-md p-0.5">
                {[
                    { v: "broadcast", label: "All devices" },
                    { v: "user", label: "Specific user" },
                ].map((opt) => (
                    <button
                        key={opt.v}
                        data-testid={`test-push-${tenant.slug}-mode-${opt.v}`}
                        onClick={() => { setMode(opt.v); setResult(null); }}
                        className={`flex-1 text-[10px] font-bold py-1 rounded ${
                            mode === opt.v
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >{opt.label}</button>
                ))}
            </div>

            {mode === "user" && (
                <select
                    data-testid={`test-push-${tenant.slug}-user`}
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    disabled={usersLoading}
                    className="w-full text-[11px] rounded-md bg-card border border-border px-2 py-1.5"
                >
                    <option value="">
                        {usersLoading ? "Loading users…"
                            : users.length === 0 ? "No admin / owner / rider users yet"
                            : `Pick a user (${users.length})`}
                    </option>
                    {users.map((u) => {
                        const tokens = u.total_tokens;
                        const tokenInfo = tokens === 0 ? "no devices"
                            : `${u.customer_tokens}c · ${u.rider_tokens}r`;
                        return (
                            <option key={u.id} value={u.id}>
                                {u.role.replace("_", " ")} — {u.name} ({tokenInfo})
                            </option>
                        );
                    })}
                </select>
            )}

            <div className="flex items-center gap-1">
                <select
                    data-testid={`test-push-${tenant.slug}-scope`}
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    className="flex-1 text-[11px] rounded-md bg-card border border-border px-2 py-1.5"
                >
                    <option value="all">All apps (customer + rider)</option>
                    <option value="customer">Customer app only</option>
                    <option value="rider">Rider app only</option>
                </select>
                <button
                    data-testid={`test-push-${tenant.slug}-send`}
                    onClick={mode === "broadcast" ? sendBroadcast : sendToUser}
                    disabled={sending || (mode === "user" && !userId)}
                    className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-50"
                >
                    {sending ? <Loader2 size={11} className="animate-spin" /> : <BellRing size={11} />}
                    Send
                </button>
            </div>

            {result && (
                <div className="text-[10px] space-y-1" data-testid={`test-push-${tenant.slug}-result`}>
                    {result.kind === "user" ? (
                        <div className="text-muted-foreground">
                            User: <span className="text-foreground font-semibold">{result.user_name}</span> ·
                            Scope: <span className="font-mono text-foreground">{result.scope}</span> ·
                            FCM: <span className="font-mono text-emerald-600">{result.sent_fcm || 0}</span>/<span className="font-mono text-red-600">{result.failed_fcm || 0}</span> ·
                            Web: <span className="font-mono text-emerald-600">{result.sent_web || 0}</span>/<span className="font-mono text-red-600">{result.failed_web || 0}</span>
                            {result.deep_link && (
                                <span className="ml-1">· link: <span className="font-mono text-foreground">{result.deep_link}</span></span>
                            )}
                        </div>
                    ) : (
                        <div className="text-muted-foreground">
                            Scope: <span className="font-mono text-foreground">{result.scope}</span> ·
                            Total: <span className="font-mono text-foreground">{result.total}</span> ·
                            Sent: <span className="font-mono text-emerald-600">{result.sent}</span> ·
                            Failed: <span className="font-mono text-red-600">{result.failed}</span>
                        </div>
                    )}
                    {result.hint && (
                        <div className="text-amber-700 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-2 py-1.5 rounded-md">
                            {result.hint}
                        </div>
                    )}
                    {result.last_error && result.kind === "user" && (
                        <div className="text-red-700 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-2 py-1.5 rounded-md font-mono">
                            {typeof result.last_error === "string"
                                ? result.last_error
                                : (result.last_error.error?.status
                                   || result.last_error.error?.message
                                   || JSON.stringify(result.last_error).slice(0, 200))}
                        </div>
                    )}
                    {(result.results || []).length > 0 && (
                        <div className="max-h-32 overflow-auto bg-muted/50 rounded-md p-1.5 space-y-0.5">
                            {result.results.map((r, i) => (
                                <div key={`${r.app}-${r.platform}-${r.token_tail}-${i}`} className="flex items-center justify-between gap-2 font-mono">
                                    <span className={r.ok ? "text-emerald-600" : "text-red-600"}>
                                        {r.ok ? "✓" : "✗"}
                                    </span>
                                    <span className="flex-1 truncate text-foreground">
                                        {r.app}·{r.platform}·…{r.token_tail}
                                    </span>
                                    {!r.ok && (
                                        <span className="text-red-600 truncate max-w-[180px]" title={r.error}>
                                            {r.error}{r.pruned ? " · pruned" : ""}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function MiniField({ label, value, onChange, placeholder, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</span>
            <input
                data-testid={testid}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-card border border-border text-[11px] font-mono"
            />
        </label>
    );
}


function BrandingPanel({ tenant, plans = [], onSaved }) {
    const branding = tenant.branding || {};
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: tenant.name || "",
        contact_mobile: tenant.contact_mobile || "",
        custom_domain: tenant.custom_domain || "",
        marketplace_alias_domain: tenant.marketplace_alias_domain || "",
        plan: tenant.plan || "STARTER",
        primary_hsl: branding.primary_hsl || "",
        accent_hsl: branding.accent_hsl || "",
        logo_url: branding.logo_url || "",
        customer_logo_url: branding.customer_logo_url || "",
        rider_logo_url: branding.rider_logo_url || "",
    });
    const [busy, setBusy] = useState(false);

    // Re-hydrate form state when the parent reloads the tenant list (e.g.
    // after another panel saved). Keeps the inputs in sync with the latest
    // server-side values.
    useEffect(() => {
        const b = tenant.branding || {};
        setForm({
            name: tenant.name || "",
            contact_mobile: tenant.contact_mobile || "",
            custom_domain: tenant.custom_domain || "",
            plan: tenant.plan || "STARTER",
            primary_hsl: b.primary_hsl || "",
            accent_hsl: b.accent_hsl || "",
            logo_url: b.logo_url || "",
            customer_logo_url: b.customer_logo_url || "",
            rider_logo_url: b.rider_logo_url || "",
        });
    }, [tenant]);

    const save = async () => {
        setBusy(true);
        try {
            const upd = {};
            // Top-level fields: only patch what actually changed.
            if (form.name && form.name !== tenant.name) upd.name = form.name;
            if (form.plan && form.plan !== tenant.plan) upd.plan = form.plan;
            if (form.custom_domain !== (tenant.custom_domain || "")) {
                // Normalise: strip leading scheme + trailing slash, lowercase.
                const cd = (form.custom_domain || "")
                    .replace(/^https?:\/\//i, "")
                    .replace(/\/+$/, "")
                    .trim()
                    .toLowerCase();
                upd.custom_domain = cd || null;
            }
            // iter64 — marketplace_alias_domain change (same normalisation).
            if (form.marketplace_alias_domain !== (tenant.marketplace_alias_domain || "")) {
                const mad = (form.marketplace_alias_domain || "")
                    .replace(/^https?:\/\//i, "")
                    .replace(/\/+$/, "")
                    .trim()
                    .toLowerCase();
                upd.marketplace_alias_domain = mad || null;
            }
            // Branding subdoc — server merges via dot-notation, so passing
            // the full nested object is safe; we only include changed keys
            // so blank-on-blank doesn't blow away an existing colour.
            const brandingPatch = {};
            for (const k of ["primary_hsl", "accent_hsl", "logo_url",
                             "customer_logo_url", "rider_logo_url"]) {
                if (form[k] !== (branding[k] || "")) brandingPatch[k] = form[k] || null;
            }
            if (Object.keys(brandingPatch).length > 0) upd.branding = brandingPatch;

            if (Object.keys(upd).length === 0) {
                setOpen(false);
                return;
            }
            await api.patch(`/super/tenants/${tenant.id}`, upd);
            toast.success("Tenant updated");
            onSaved?.();
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid={`branding-panel-${tenant.slug}`}>
            <button
                data-testid={`branding-panel-${tenant.slug}-toggle`}
                onClick={() => setOpen((x) => !x)}
                className="w-full text-left flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground"
            >
                <span className="inline-flex items-center gap-1.5">
                    <ImageIcon size={11} /> Branding &amp; details
                </span>
                <span>{open ? "▾" : "▸"}</span>
            </button>
            {open && (
                <div className="mt-2 space-y-2 text-xs">
                    <MiniField
                        label="Display name"
                        testid={`branding-${tenant.slug}-name`}
                        value={form.name}
                        onChange={(v) => setForm({ ...form, name: v })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <MiniField
                            label="Primary HSL"
                            testid={`branding-${tenant.slug}-primary`}
                            placeholder="142 71% 45%"
                            value={form.primary_hsl}
                            onChange={(v) => setForm({ ...form, primary_hsl: v })}
                        />
                        <MiniField
                            label="Accent HSL"
                            testid={`branding-${tenant.slug}-accent`}
                            placeholder="39 100% 50%"
                            value={form.accent_hsl}
                            onChange={(v) => setForm({ ...form, accent_hsl: v })}
                        />
                    </div>
                    <div>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Plan</span>
                        <select
                            data-testid={`branding-${tenant.slug}-plan`}
                            value={form.plan}
                            onChange={(e) => setForm({ ...form, plan: e.target.value })}
                            className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-card border border-border text-[11px] font-semibold"
                        >
                            {/* Pull plans live from /super/plans so any custom
                                plan the super-admin defined is selectable here.
                                Tenant-specific custom plans (is_custom + tenant_id)
                                are also surfaced — for that tenant only. */}
                            {(() => {
                                const usable = plans.filter((p) =>
                                    p.status !== "ARCHIVED" &&
                                    (!p.is_custom || p.tenant_id === tenant.id),
                                );
                                if (usable.length === 0) {
                                    return ["STARTER", "GROWTH", "ENTERPRISE"].map((c) => (
                                        <option key={c} value={c}>{c[0] + c.slice(1).toLowerCase()}</option>
                                    ));
                                }
                                // Keep the currently-set plan code selectable even
                                // if it's been archived since assignment.
                                const codes = new Set(usable.map((p) => p.code));
                                const extras = !codes.has(form.plan) && form.plan
                                    ? [{ id: "_legacy", code: form.plan, name: `${form.plan} (legacy)` }]
                                    : [];
                                return [...usable, ...extras].map((p) => (
                                    <option key={p.id} value={p.code}>
                                        {p.name}{p.is_custom ? " · custom" : ""}
                                    </option>
                                ));
                            })()}
                        </select>
                    </div>
                    <div className="pt-1 space-y-3">
                        <LogoUploader
                            label="Storefront logo"
                            sublabel="Shown on the web storefront + used as fallback when an app-specific logo isn't set"
                            testid={`branding-${tenant.slug}-logo-storefront`}
                            value={form.logo_url}
                            onChange={(url) => setForm({ ...form, logo_url: url })}
                        />
                        <LogoUploader
                            label="Customer app logo"
                            sublabel="Shown on the Customer mobile app icon (Play Store / App Store)"
                            testid={`branding-${tenant.slug}-logo-customer`}
                            value={form.customer_logo_url}
                            onChange={(url) => setForm({ ...form, customer_logo_url: url })}
                        />
                        <LogoUploader
                            label="Rider app logo"
                            sublabel="Shown on the Rider mobile app icon (Play Store / App Store)"
                            testid={`branding-${tenant.slug}-logo-rider`}
                            value={form.rider_logo_url}
                            onChange={(url) => setForm({ ...form, rider_logo_url: url })}
                        />
                    </div>
                    <CustomDomainEditor
                        tenant={tenant}
                        value={form.custom_domain}
                        onChange={(v) => setForm({ ...form, custom_domain: v })}
                    />
                    {/* iter64 — Marketplace-alias domain (per-tenant opt-in for the new parent domain) */}
                    <div className="border border-border rounded-lg p-2 bg-muted/20">
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                            Marketplace alias domain (iter64)
                        </label>
                        <input
                            data-testid={`tenant-${tenant.slug}-marketplace-alias`}
                            type="text"
                            placeholder="marketplace.localappstore.in"
                            value={form.marketplace_alias_domain}
                            onChange={(e) => setForm({ ...form, marketplace_alias_domain: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs border border-border rounded-md bg-card font-mono"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                            When set, ``&lt;slug&gt;.&lt;this-host&gt;`` ALSO resolves to this tenant in addition to the legacy parent (``food.localappstore.in``). Use during the platform-domain migration so each tenant flips on their own schedule. Leave blank to skip.
                        </p>
                    </div>
                    <button
                        data-testid={`branding-${tenant.slug}-save`}
                        onClick={save}
                        disabled={busy}
                        className="mt-2 w-full bg-foreground text-background rounded-lg py-2 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                        {busy && <Loader2 size={11} className="animate-spin" />} Save branding
                    </button>
                </div>
            )}
        </div>
    );
}

function CustomDomainEditor({ tenant, value, onChange }) {
    const [showHelp, setShowHelp] = useState(false);
    const [autoBusy, setAutoBusy] = useState(false);
    const [platformApex, setPlatformApex] = useState("");
    const cdHost = (value || "").replace(/^https?:\/\//i, "").replace(/\/+$/, "");
    // The actual host serving the React app. CNAME records for tenant
    // subdomains MUST point here — NOT to the bare apex (which has no
    // app server attached).
    const platformHost = typeof window !== "undefined" ? window.location.host : "food.localappstore.in";

    // Pull the "default CNAME target" from platform settings so the
    // subdomain stitcher uses the operator's configured apex (e.g.
    // ``localappstore.in``). Falls back to deriving the apex from the
    // current host so the editor is useful even before settings are saved.
    useEffect(() => {
        let alive = true;
        api.get("/super/settings/platform").then(({ data }) => {
            if (!alive) return;
            const tgt = (data?.default_cname_target || "").trim();
            // The operator's "default CNAME target" is the right-hand-side
            // of the CNAME record (where the subdomain POINTS TO). For the
            // subdomain SUGGESTION we want the apex they own — usually the
            // tail of the same string. e.g. target=``food.localappstore.in``
            // → suggest ``{slug}.localappstore.in``. If target is a bare
            // apex (``localappstore.in``) we use it as-is.
            const apex = tgt
                ? tgt.split(".").slice(-2).join(".")
                : platformHost.split(".").slice(-2).join(".");
            setPlatformApex(apex);
        }).catch(() => {
            setPlatformApex(platformHost.split(".").slice(-2).join("."));
        });
        return () => { alive = false; };
    }, [platformHost]);

    // Suggested FQDN when nothing is typed yet — apnisavings →
    // ``apnisavings.localappstore.in``. Lets the operator one-click the
    // suggestion instead of typing the full hostname.
    const suggested = platformApex ? `${tenant.slug}.${platformApex}` : "";
    const showSuggestion = !cdHost && !!suggested;

    const autoMap = async () => {
        // If the operator hasn't typed anything yet, push the suggested
        // FQDN. Otherwise honour whatever they typed.
        const targetFqdn = cdHost || suggested;
        const fqdn = targetFqdn.split(".");
        if (fqdn.length < 2) {
            toast.error("Type a full hostname first, e.g. shop.example.com");
            return;
        }
        const subdomain = fqdn[0];
        const domain = fqdn.slice(1).join(".");
        if (!window.confirm(
            `Push this CNAME record to GoDaddy automatically?\n\n` +
            `  ${subdomain}.${domain}   →   ${platformHost}\n\n` +
            `(The CNAME will point at the actual app server, not at the registrar root.)`
        )) return;
        setAutoBusy(true);
        try {
            const { data } = await api.post("/super/godaddy/map", {
                tenant_id: tenant.id, domain, subdomain,
                target: platformHost,                  // always point at the live app host
            });
            toast.success(`✓ CNAME ${data.fqdn} → ${data.target} pushed`);
            onChange(data.fqdn);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed to push CNAME";
            if (msg.includes("credentials not configured")) {
                toast.error("Configure GoDaddy API credentials in Super Admin → Settings first");
            } else {
                toast.error(msg);
            }
        } finally { setAutoBusy(false); }
    };

    const buttonDisabled = autoBusy || (!cdHost.includes(".") && !suggested);

    return (
        <div className="pt-2 border-t border-dashed border-border" data-testid={`custom-domain-${tenant.slug}`}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Custom domain</span>
                <button
                    type="button"
                    onClick={() => setShowHelp((x) => !x)}
                    data-testid={`custom-domain-${tenant.slug}-help-toggle`}
                    className="text-[10px] text-primary font-semibold"
                >
                    {showHelp ? "Hide DNS setup" : "How to set up DNS"}
                </button>
            </div>
            <input
                data-testid={`custom-domain-${tenant.slug}-input`}
                className="inp"
                placeholder={suggested ? `e.g. ${suggested}` : `e.g. shop.${tenant.slug}.com`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
            {showSuggestion && (
                <div
                    className="mt-1.5 px-2.5 py-2 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 rounded-lg text-[11px] inline-flex items-center justify-between gap-2 w-full"
                    data-testid={`custom-domain-${tenant.slug}-suggestion`}
                >
                    <span className="text-sky-900 dark:text-sky-200">
                        Suggested: <code className="font-mono font-bold">{suggested}</code>
                    </span>
                    <button
                        type="button"
                        onClick={() => onChange(suggested)}
                        className="text-[10px] font-bold text-sky-700 dark:text-sky-300 underline"
                        data-testid={`custom-domain-${tenant.slug}-use-suggested`}
                    >
                        Use this →
                    </button>
                </div>
            )}
            <div className="flex gap-2 mt-1.5">
                <button
                    type="button"
                    onClick={autoMap}
                    disabled={buttonDisabled}
                    data-testid={`custom-domain-${tenant.slug}-automap`}
                    className="flex-1 bg-emerald-600 text-white rounded-lg py-2 text-[11px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    title={cdHost
                        ? `Push CNAME ${cdHost} → ${platformHost} to GoDaddy`
                        : suggested
                            ? `Push CNAME ${suggested} → ${platformHost} to GoDaddy`
                            : "Enter a hostname first"}
                >
                    {autoBusy ? <Loader2 size={11} className="animate-spin" /> : <Globe size={11} />}
                    Auto-link {cdHost || suggested || ""} with GoDaddy
                </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
                The CNAME points <code>{cdHost || suggested || `<tenant-host>`}</code> →{" "}
                <code className="font-bold">{platformHost}</code>{" "}
                so customers can hit your tenant page directly — no <code>?tenant={tenant.slug}</code> needed.
            </p>
            {showHelp && (
                <div className="mt-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-[11px] space-y-2" data-testid={`custom-domain-${tenant.slug}-help`}>
                    <p className="font-semibold text-amber-900 dark:text-amber-200">DNS setup on GoDaddy / Namecheap / Cloudflare</p>
                    <ol className="list-decimal pl-4 space-y-1 text-amber-900/90 dark:text-amber-200/90">
                        <li>Log in to your domain registrar and open the DNS panel for <code>{tenant.slug}.com</code> (or whichever domain you own).</li>
                        <li>Add a <b>CNAME</b> record:
                            <div className="bg-amber-100 dark:bg-amber-950/50 rounded mt-1 p-1.5 font-mono text-[10px]">
                                <div>Type: <b>CNAME</b></div>
                                <div>Host / Name: <b>{cdHost ? cdHost.split(".")[0] : (suggested ? suggested.split(".")[0] : "shop")}</b> (or <code>@</code> for the root)</div>
                                <div>Points to / Value: <b>{platformHost}</b></div>
                                <div>TTL: <b>1 hour</b></div>
                            </div>
                        </li>
                        <li>Save and wait 5–30 minutes for DNS propagation.</li>
                        <li>Come back here, paste the full hostname above (e.g. <code>{cdHost || suggested || `shop.${tenant.slug}.com`}</code>), then click <b>Save branding</b>.</li>
                        <li>Visit <code>https://{cdHost || suggested || `shop.${tenant.slug}.com`}/</code> — the storefront for <b>{tenant.name}</b> should load directly.</li>
                    </ol>
                    <p className="text-amber-900/70 dark:text-amber-200/70">
                        ⚠️ SSL certificate provisioning happens automatically via Emergent ingress
                        once the CNAME is verified. If the cert fails, raise a ticket with Emergent Support.
                    </p>
                    <p className="text-amber-900/70 dark:text-amber-200/70">
                        💡 Want to skip these steps? Configure the platform GoDaddy API key
                        under <b>Super Admin → Settings</b>, then use the green
                        &ldquo;<b>Auto-link with GoDaddy</b>&rdquo; button above.
                    </p>
                    <p className="text-amber-900/70 dark:text-amber-200/70">
                        🛠️ Self-hosting outside Emergent? Point the DNS record at your server and
                        serve the same static build folder used for every other tenant — no
                        per-tenant server config needed. The app resolves which tenant a custom
                        domain belongs to on its own, by asking the backend directly.
                    </p>
                </div>
            )}
        </div>
    );
}


function LogoUploader({ value, onChange, label, sublabel, testid }) {
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please pick an image file");
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("Image is larger than 10 MB");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            // ``/uploads/logo`` server-pads + resizes to exactly 1024×1024
            // PNG so the build script always has a clean master image.
            const { data } = await api.post("/uploads/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange?.(data.url);
            toast.success("Logo uploaded (auto-resized to 1024×1024)");
        } catch (err) {
            // The /api/uploads endpoint returns 503 when S3 isn't configured —
            // surface that to the super-admin so they go set up AWS keys first.
            const detail = err?.response?.data?.detail || err?.message || "Upload failed";
            toast.error(detail, { duration: 8000 });
        } finally {
            setBusy(false);
            // Allow re-uploading the same file (input.onChange only fires when
            // the value changes).
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div data-testid={testid || "tenant-logo-uploader"}>
            <span className="text-xs font-bold text-muted-foreground flex items-center gap-1 mb-1.5">
                <ImageIcon size={12} /> {label || "App logo"}
                <span className="font-normal text-[10px]">
                    {sublabel || "(uploads stored as 1024×1024 PNG · source ≥512×512 recommended)"}
                </span>
            </span>
            <div className="flex items-center gap-3">
                {value ? (
                    <div className="relative">
                        <img
                            src={value}
                            alt="logo preview"
                            data-testid="tenant-logo-preview"
                            className="w-20 h-20 rounded-xl object-cover border border-border bg-card"
                            onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <button
                            type="button"
                            data-testid="tenant-logo-remove"
                            onClick={() => onChange?.("")}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white inline-flex items-center justify-center shadow-md"
                            title="Remove logo"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ) : (
                    <div className="w-20 h-20 rounded-xl border border-dashed border-border flex items-center justify-center bg-muted text-muted-foreground" data-testid="tenant-logo-empty">
                        <ImageIcon size={20} />
                    </div>
                )}
                <div className="flex-1 space-y-1">
                    <button
                        type="button"
                        data-testid="tenant-logo-upload-btn"
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-foreground text-background hover:opacity-90 disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <UploadIcon size={12} />}
                        {value ? "Replace" : "Upload logo"}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        onChange={onPick}
                        className="hidden"
                        data-testid="tenant-logo-file-input"
                    />
                    <p className="text-[10px] text-muted-foreground">
                        Used everywhere: PWA icon, Play Store listing, native app launcher, 512×512 +
                        1024×500 Play Store feature graphic.
                    </p>
                </div>
            </div>
        </div>
    );
}


/**
 * iter70 — Store Submission URLs panel.
 *
 * Renders the 4 legal URLs the Super Admin needs when filing a Play
 * Store / App Store listing for this tenant:
 *
 *     Customer app   ·  Privacy Policy   ·  Terms & Conditions
 *     Rider app      ·  Privacy Policy   ·  Terms & Conditions
 *
 * Each row has a one-click Copy button. Collapsed by default to keep
 * the dense tenant card scannable — expanded by clicking the heading.
 */
function StoreLegalUrlsPanel({ tenant, onCopy }) {
    const [open, setOpen] = useState(false);
    const urls = legalUrls(tenant?.slug);
    if (!urls) return null;
    return (
        <div className="mt-3 pt-3 border-t border-border">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`legal-urls-toggle-${tenant.slug}`}
                className="w-full flex items-center justify-between text-left text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground"
            >
                <span className="inline-flex items-center gap-1.5">
                    <FileText size={11} />
                    Store submission URLs
                </span>
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
            {open && (
                <div className="mt-2.5 space-y-2" data-testid={`legal-urls-body-${tenant.slug}`}>
                    <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center gap-1">
                            <Smartphone size={10} /> Customer app
                        </p>
                        <LegalUrlRow slug={tenant.slug} label="Privacy" url={urls.customer_privacy} testidKey="customer-privacy" onCopy={onCopy} />
                        <LegalUrlRow slug={tenant.slug} label="Terms"   url={urls.customer_terms}   testidKey="customer-terms"   onCopy={onCopy} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center gap-1 mt-2">
                            <Bike size={10} /> Rider app
                        </p>
                        <LegalUrlRow slug={tenant.slug} label="Privacy" url={urls.rider_privacy} testidKey="rider-privacy" onCopy={onCopy} />
                        <LegalUrlRow slug={tenant.slug} label="Terms"   url={urls.rider_terms}   testidKey="rider-terms"   onCopy={onCopy} />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                        Paste these into the Play Store / App Store listing for each app build. The same MD content is served today; the <code className="bg-muted px-1 rounded">?app=rider</code> flag keeps the URL unique per submission so we can later return rider-specific policies without re-submitting.
                    </p>
                </div>
            )}
        </div>
    );
}

/** Single legal URL row (label · readonly input · copy · open). Defined
 *  at module scope to keep React from re-mounting the input on every
 *  parent render. */
function LegalUrlRow({ slug, label, url, testidKey, onCopy }) {
    return (
        <div className="flex items-center gap-2 py-1.5" data-testid={`legal-url-row-${testidKey}-${slug}`}>
            <p className="text-[11px] font-semibold text-muted-foreground w-20 flex-shrink-0">{label}</p>
            <input
                readOnly
                value={url}
                data-testid={`legal-url-input-${testidKey}-${slug}`}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 bg-muted/60 border border-border rounded-md px-2 py-1.5 text-[11px] font-mono text-foreground/90"
            />
            <button
                type="button"
                onClick={() => onCopy(url)}
                data-testid={`legal-url-copy-${testidKey}-${slug}`}
                title="Copy URL"
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-primary"
            >
                <Copy size={12} />
            </button>
            <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid={`legal-url-open-${testidKey}-${slug}`}
                title="Open in new tab"
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-primary"
            >
                <ExternalLink size={12} />
            </a>
        </div>
    );
}
